import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { interval } from 'rxjs/internal/observable/interval';
import { merge } from 'rxjs/internal/observable/merge';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';
import { map, tap, throttleTime } from 'rxjs/operators';
import { SingletonCheckHelper } from '../helpers/singleton-check.helper';
import { ReturnUrlInfo } from '../internals/return-url-info';
import { updateSystemPreferencesInternalConfig } from '../internals/system-preferences-config.internal';
import { AuthenticatedUserViewModel } from '../models/logins/authenticated-user-view-model';
import { RoleAccessSettings } from '../models/roles';
import { AuthenticationStorageService } from './system/authentication-storage.service';
import { LoginApiService } from './system/login-api.service';
import { LoginComponentService } from './system/login-component.service';
import { ModalStack } from '../modules/tcm-core.module/internals/modal-stack';

@Injectable()
export class AuthService implements OnDestroy {

  currentUser$: Observable<AuthenticatedUserViewModel>;

  get currentUserValue(): AuthenticatedUserViewModel {
    const user = this._currentUser.value;

    if (!user || new Date(user.tokenExpiresAt) < new Date()) {
      return null;
    }

    return user;
  }

  get currentPermissions(): RoleAccessSettings {
    return this._currentUser && this._currentUser.value ? this._currentUser.value.permissions : null;
  }

  private readonly _currentUser: BehaviorSubject<AuthenticatedUserViewModel>;

  private monitorTokenSubscription: Subscription;
  private userInteractionSubscription: Subscription;

  private isInitialized: boolean;

  constructor(
    private readonly storage: AuthenticationStorageService,
    private readonly loginComponentService: LoginComponentService,
    private readonly loginApiService: LoginApiService,
    @Inject(DOCUMENT) private document: Document) {

    SingletonCheckHelper.ensureSingleton(this);

    const userFromStorage = this.storage.getCurrentUser();

    this.setupCurrentUser(userFromStorage, false);

    this._currentUser = new BehaviorSubject<AuthenticatedUserViewModel>(userFromStorage);

    this.currentUser$ = this._currentUser.asObservable();
  }

  init(): void {

    if (this.isInitialized === true)
      return;

    this.isInitialized = true;

    this.monitorToken();

    // Subscribe to user interaction events
    const eventStreams = ['mousemove', 'mousedown', 'keydown', 'focus'].map((ev) => fromEvent(this.document, ev));

    this.userInteractionSubscription = merge(...eventStreams)
      .pipe(
        throttleTime(10000),
        tap(_ => this.prolongSession()))
      .subscribe();
  }


  ngOnDestroy() {
    this.monitorTokenSubscription.unsubscribe();
    this.userInteractionSubscription.unsubscribe();
  }

  private async prolongSession() {

    const currentDateTime = new Date();
    const user = this.storage.getCurrentUser();

    if (!user)
      return;

    const tokenExpiresAfter = new Date(user.tokenExpiresAt);

    // check token is not outdated. If true, do nothing
    if (currentDateTime > tokenExpiresAfter)
      return;

    const diffMs = tokenExpiresAfter.getTime() - currentDateTime.getTime();
    var remainingMins = ((diffMs % 86400000) % 3600000) / 60000;

    if (remainingMins > (user.tokenDurationMinutes / 2))
      return;

    console.log('Prolonging Session');
    
    // Refresh token
    await this.loginApiService.refreshTokenAsync();
  }

  /**
   * Subscribe to current user changed event
   */
  private monitorToken() {

    const monitorEvents = merge(
      interval(1000).pipe(map(_ => this.storage.getCurrentUser())),
      this.storage.onCurrentUserChanged$);

    this.monitorTokenSubscription = monitorEvents.subscribe(user => {

      if (!user && this._currentUser.value) {
        console.log('Token doesn\'t exist - logging out');

        // Close all modals before navigating to login
        ModalStack.dismissAllModals('Session timeout');

        this.setupCurrentUser(null);

        const returnUrl = document.location.pathname + document.location.search;

        const returnUrlInfo: ReturnUrlInfo = { returnUrl: returnUrl };

        this.loginComponentService.navigateToLogin(returnUrlInfo);
      }
      else if (user) {
        if (new Date(user.tokenExpiresAt) < new Date()) {
          console.log('Token expired - logging out');

          // Close all modals before showing login modal
          ModalStack.dismissAllModals('Session timeout');

          this.deleteCurrentUser();

          this.loginComponentService.openLoginModal();

        } else if (!this._currentUser.value) {
          console.log('Token found - logging in');

          this.setupCurrentUser(user);

          this.loginComponentService.closeLogin();
        }
        else if (this._currentUser.value.tokenExpiresAt != user.tokenExpiresAt) {
          // refresh user and permissions
          this.setupCurrentUser(user);

          this.loginComponentService.closeLogin();
        }
      }
    });
  }

  private deleteCurrentUser() {
    this.setupCurrentUser(null);

    this.storage.setCurrentUser(null);
  }

  private setupCurrentUser(user: AuthenticatedUserViewModel, emit = true) {
    if (user) {
      updateSystemPreferencesInternalConfig(user.systemPreferences);
    }

    if (emit) {
      this._currentUser.next(user);
    }
  }
}
